import { DateTime } from 'luxon'
import { AssignableQuestionnaire, PatientHistoryType } from 'src/graphql.generated'
import { PatientHistoryEntry } from '../api/useAdvancedPatientHistory'
import { formatDay, formatDayTimeIn, toIso } from '../format'
import { valueOf } from '../panels/patientHistory/EntryBody'
import { advancedColors } from '../theme'

export type InstanceAnswer = { id: string; question: string; answer: string }

export type ScoredInstance = {
  id: string
  date: string | null
  createdAt: string | null
  score: number | null
  answers: InstanceAnswer[]
}

type Question = { key: string; label: string }

const questions = (prefix: string, labels: string[]): Question[] =>
  labels.map((label, index) => ({ key: `${prefix}_QUESTION_${index + 1}`, label }))

export const HISTORY_TYPE: Record<AssignableQuestionnaire, PatientHistoryType> = {
  [AssignableQuestionnaire.Pcl5]: PatientHistoryType.Pcl5,
  [AssignableQuestionnaire.Asrm]: PatientHistoryType.Asrm,
  [AssignableQuestionnaire.Ocir]: PatientHistoryType.Ocir,
  [AssignableQuestionnaire.Isi]: PatientHistoryType.Isi,
}

export const SCORE_PREFIX: Record<AssignableQuestionnaire, string> = {
  [AssignableQuestionnaire.Pcl5]: 'PCL5',
  [AssignableQuestionnaire.Asrm]: 'ASRM',
  [AssignableQuestionnaire.Ocir]: 'OCIR',
  [AssignableQuestionnaire.Isi]: 'ISI',
}

export const QUESTIONNAIRE_QUESTIONS: Record<AssignableQuestionnaire, Question[]> = {
  [AssignableQuestionnaire.Pcl5]: questions('PCL5', [
    'Repeated, disturbing, and unwanted memories of the stressful experience',
    'Repeated, disturbing dreams of the stressful experience',
    'Suddenly feeling or acting as if the stressful experience were actually happening again',
    'Feeling very upset when something reminded you of the stressful experience',
    'Having strong physical reactions when something reminded you of the stressful experience',
    'Avoiding memories, thoughts, or feelings related to the stressful experience',
    'Avoiding external reminders of the stressful experience',
    'Trouble remembering important parts of the stressful experience',
    'Having strong negative beliefs about yourself, other people, or the world',
    'Blaming yourself or someone else for the stressful experience or what happened after it',
    'Having strong negative feelings such as fear, horror, anger, guilt, or shame',
    'Loss of interest in activities that you used to enjoy',
    'Feeling distant or cut off from other people',
    'Trouble experiencing positive feelings',
    'Irritable behavior, angry outbursts, or acting aggressively',
    'Taking too many risks or doing things that could cause you harm',
    'Being "superalert" or watchful or on guard',
    'Feeling jumpy or easily startled',
    'Having difficulty concentrating',
    'Trouble falling or staying asleep',
  ]),
  [AssignableQuestionnaire.Asrm]: questions('ASRM', [
    'Positive mood',
    'Self-confidence',
    'Sleep patterns',
    'Speech',
    'Activity level',
  ]),
  [AssignableQuestionnaire.Ocir]: questions('OCIR', [
    'I have saved up so many things that they get in the way',
    'I check things more often than necessary',
    'I get upset if objects are not arranged properly',
    'I feel compelled to count while I am doing things',
    'I find it difficult to touch an object when I know it has been touched by strangers or certain people',
    'I find it difficult to control my own thoughts',
    'I collect things I do not need',
    'I repeatedly check doors, windows, drawers, etc.',
    'I get upset if others change the way I have arranged things',
    'I feel I have to repeat certain numbers',
    'I sometimes have to wash or clean myself simply because I feel contaminated',
    'I am upset by unpleasant thoughts that come into my mind against my will',
    'I avoid throwing things away because I am afraid I might need them later',
    'I repeatedly check gas and water taps and light switches after turning them off',
    'I need things to be arranged in a particular order',
    'I feel that there are good and bad numbers',
    'I wash my hands more often and longer than necessary',
    'I frequently get nasty thoughts and have difficulty getting rid of them',
  ]),
  [AssignableQuestionnaire.Isi]: questions('ISI', [
    'Difficulty falling asleep',
    'Difficulty staying asleep',
    'Problems waking up too early',
    'Satisfaction with current sleep pattern',
    'How noticeable to others the sleep problem is',
    'Worry or distress about the sleep problem',
    'Interference with daily functioning',
  ]),
}

const asNumber = (value: unknown): number | null => (typeof value === 'number' && Number.isFinite(value) ? value : null)

const millis = (iso: string | null): number | null => {
  if (!iso) {
    return null
  }
  const date = DateTime.fromISO(iso)

  return date.isValid ? date.toMillis() : null
}

export const scoreFromValue = (
  questionnaire: AssignableQuestionnaire,
  value: Record<string, unknown>
): number | null => {
  const prefix = SCORE_PREFIX[questionnaire]
  const stored = asNumber(value[`${prefix}_SCORE`])
  if (stored !== null) {
    return stored
  }

  return QUESTIONNAIRE_QUESTIONS[questionnaire].reduce<number | null>((total, question) => {
    const answer = asNumber(value[question.key])

    return answer === null ? total : (total ?? 0) + answer
  }, null)
}

export const answersFromValue = (
  questionnaire: AssignableQuestionnaire,
  value: Record<string, unknown>
): InstanceAnswer[] =>
  QUESTIONNAIRE_QUESTIONS[questionnaire].flatMap((question) => {
    const answer = asNumber(value[question.key])
    if (answer === null) {
      return []
    }

    return [{ id: question.key, question: question.label, answer: String(answer) }]
  })

export const scoredInstancesFor = (
  questionnaire: AssignableQuestionnaire,
  entries: PatientHistoryEntry[]
): ScoredInstance[] => {
  const type = HISTORY_TYPE[questionnaire]

  return entries
    .flatMap((entry) => {
      if (entry.type !== type) {
        return []
      }
      const value = valueOf(entry)
      if (!value) {
        return []
      }
      const score = scoreFromValue(questionnaire, value)
      const answers = answersFromValue(questionnaire, value)
      if (score === null && answers.length === 0) {
        return []
      }

      return [
        {
          id: entry.entryID,
          date: toIso(entry.date),
          createdAt: toIso(entry.createdAt),
          score,
          answers,
        },
      ]
    })
    .sort((left, right) => (millis(right.createdAt ?? right.date) ?? 0) - (millis(left.createdAt ?? left.date) ?? 0))
}

export const completionDayLabel = (instance: ScoredInstance, timezone: string | null | undefined): string => {
  const date = instance.createdAt && timezone ? DateTime.fromISO(instance.createdAt, { zone: timezone }) : null

  return date?.isValid ? date.toFormat('d LLL yyyy') : formatDay(instance.date)
}

export const completionTimeLabel = (instance: ScoredInstance, timezone: string | null | undefined): string =>
  formatDayTimeIn(instance.createdAt, timezone) ?? `${completionDayLabel(instance, timezone)} · Time unavailable`

export const resolvedInstanceId = (instanceId: string, instances: { id: string }[]): string =>
  instances.some((instance) => instance.id === instanceId) ? instanceId : ''

// Lower score is better for these instruments — a drop vs the previous instance is an improvement (ADR-074).
export const vsPreviousPercent = (latest: number | null, previous: number | null): number | null => {
  if (latest === null || previous === null || previous === 0) {
    return null
  }

  return Math.round(((previous - latest) / previous) * 100)
}

export const vsPreviousLabel = (changePercent: number | null): string => {
  if (changePercent === null || changePercent === 0) {
    return '—'
  }

  return changePercent > 0 ? `↓ ${changePercent}% improvement` : `↑ ${Math.abs(changePercent)}% worsening`
}

export const changeColor = (changePercent: number | null): string => {
  if (changePercent === null || changePercent === 0) {
    return advancedColors.inkMuted
  }

  return changePercent > 0 ? advancedColors.green : advancedColors.amber
}
